import React, { useEffect, useRef, useState } from 'react';
import { 
  Box, 
  Container, 
  Alert, 
  Button,
  Typography,
  Fade
} from '@mui/material';
import { RefreshOutlined } from '@mui/icons-material';
import ChatHeader from './ChatHeader';
import MessageBubble from './MessageBubble';
import MessageInput from './MessageInput';
import SessionEnded from './SessionEnded';
import PatronMenu from './PatronMenu';
import ReportModal from './ReportModal';
import OptInModal from './OptInModal';
import Toast from './Toast';
import forumsIcon from '../assets/images/forum_icon.png';
import notificationManager from '../utils/notifications';
import { useFirebaseChat } from '../hooks/useFirebaseChat';
import { parseNFCParams } from '../utils/urlParser';
import apiService from '../services/apiService';

const ChatInterface: React.FC = () => {
  const [session, setSession] = useState<any>(null);
  const [restaurant] = useState<any>(null);
  const [server] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const isInitializedRef = useRef(false);
  
  // Menu system state
  const [showMenu, setShowMenu] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showOptInModal, setShowOptInModal] = useState(false);
  const [toast, setToast] = useState<{show: boolean, message: string, severity?: 'success' | 'error' | 'warning'}>({
    show: false,
    message: ''
  });
  
  // Keyboard detection state
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  
  const clearError = () => setError(null);
  
  // Initialize from NFC or Session Hash
  const initializeFromNFC = async () => {
    console.log('🚀 Starting initialization...');
    isInitializedRef.current = true;
    
    const params = parseNFCParams();
    if (params?.nfc) {
      // Handle NFC ID - create session and redirect to hash
      await initializeFromNFCId(params.nfc);
    } else if (params?.sessionHash) {
      // Handle Session Hash - join existing session
      await initializeFromSessionHash(params.sessionHash);
    } else {
      setIsInitializing(false);
    }
  };

  // Initialize from NFC ID - get hash and redirect (simplified flow)
  const initializeFromNFCId = async (nfcId: string) => {
    setIsInitializing(true);
    try {
      setError(null);
      
      // Try to get cached patron token and validate with server
      const cacheKey = `patron_identity_${nfcId}`;
      let patronToken: string = '';
      let sessionData: any = null;
      let useExistingToken = false;
      
      
      try {
        const cachedDataString = localStorage.getItem(cacheKey);
        
        if (cachedDataString) {
          const cachedData = JSON.parse(cachedDataString);
          patronToken = cachedData.token;
          useExistingToken = true; // Always use cached token if available
        }
      } catch (error) {
        console.warn('⚠️ Token validation error:', error);
      }

      // Call new join-from-nfc endpoint (backend generates token)
      const response = await fetch('https://nfc-session-docker-wusgso4mxq-uc.a.run.app/api/sessions/join-from-nfc', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          nfcId: nfcId,
          existingToken: useExistingToken ? patronToken : undefined
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ API Error Response:', errorText);
        throw new Error(`Session join failed: ${response.status} - ${errorText}`);
      }

      sessionData = await response.json();
      
      // Backend now provides the patron token
      patronToken = sessionData.patronToken;
      
      // Store backend-generated token in localStorage
      try {
        localStorage.setItem(cacheKey, JSON.stringify({ token: patronToken, nfcId: nfcId }));
      } catch (error) {
        console.warn('⚠️ localStorage error saving token:', error);
      }

      // Validate session response structure
      if (!sessionData.success || !sessionData.sessionId) {
        throw new Error('Invalid session response structure');
      }

      // Validate required session fields
      if (!sessionData.sessionId) {
        throw new Error('Session ID is missing from response');
      }


      
      if (sessionData.sessionId && typeof sessionData.sessionId === 'string' && sessionData.sessionId.trim() !== '') {
        try {
          await joinSession(sessionData.sessionId, patronToken, sessionData.guestId, sessionData.displayName);
        } catch (joinError) {
          console.error('💥 joinSession failed:', joinError);
          throw joinError;
        }
      } else {
        console.error('❌ Session ID validation failed:', {
          sessionId: sessionData.sessionId,
          type: typeof sessionData.sessionId,
          length: sessionData.sessionId?.length
        });
        throw new Error(`Invalid session ID for Firebase chat: ${sessionData.sessionId}`);
      }

      // Update URL to session hash without reloading the page
      if (sessionData.sessionHash) {
        const hashUrl = `${window.location.origin}/${sessionData.sessionHash}`;
        history.replaceState(null, '', hashUrl);
      }
      
      setSession({
        sessionId: sessionData.sessionId,
        nfcId: nfcId,
        tableId: sessionData.restaurantId,
        tableNumber: sessionData.tableNumber,
        status: 'active'
      });
      
      setIsInitializing(false);

    } catch (err) {
      console.error('❌ NFC session creation failed:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(`Failed to create session: ${errorMessage}`);
      setIsInitializing(false); // Stop loading on error
    }
  };

  // Initialize from Session Hash - join existing session
  const initializeFromSessionHash = async (sessionHash: string) => {
    setIsInitializing(true);
    try {
      setError(null);
      
      // Get session data by hash
      const sessionData = await apiService.getSessionByHash(sessionHash);

      // Set the session data
      setSession({
        sessionId: sessionData.sessionId,
        nfcId: sessionData.nfcId,
        tableId: sessionData.restaurantId,
        tableNumber: sessionData.tableNumber,
        status: 'active'
      });

      // Try to get cached patron token and validate with server
      const cacheKey = `patron_identity_${sessionData.nfcId}`;
      let patronToken: string = '';
      let sessionData2: any = null;
      let useExistingToken = false;


      try {
        const cachedDataString = localStorage.getItem(cacheKey);

        if (cachedDataString) {
          const cachedData = JSON.parse(cachedDataString);
          patronToken = cachedData.token;
          useExistingToken = true; // Always use cached token if available
        }
      } catch (error) {
        console.warn('⚠️ Token validation error:', error);
      }

      // Call new join-from-nfc endpoint (backend generates token)
      const response = await fetch('https://nfc-session-docker-wusgso4mxq-uc.a.run.app/api/sessions/join-from-nfc', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          nfcId: sessionData.nfcId,
          existingToken: useExistingToken ? patronToken : undefined
        }),
      });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('❌ API Error Response:', errorText);
          throw new Error(`Session join failed: ${response.status} - ${errorText}`);
        }

        sessionData2 = await response.json();
        
        // Backend now provides the patron token
        patronToken = sessionData2.patronToken;
        
        // Store backend-generated token in localStorage
        try {
          localStorage.setItem(cacheKey, JSON.stringify({ token: patronToken, nfcId: sessionData.nfcId }));
        } catch (error) {
          console.warn('⚠️ localStorage error saving token:', error);
        }
      

      // Validate session response structure
      if (!sessionData2.success || !sessionData2.sessionId) {
        throw new Error('Invalid session response structure');
      }
      
      // Validate required session fields
      if (!sessionData2.sessionId) {
        throw new Error(`Invalid session ID for Firebase chat: ${sessionData2.sessionId}`);
      }


      // Join Firebase session with guest information
      await joinSession(sessionData2.sessionId, patronToken, sessionData2.guestId, sessionData2.displayName);
      
      setIsInitializing(false);

    } catch (err) {
      console.error('❌ Session hash join failed:', err);
      const errorMessage = err instanceof Error ? err.message : 'Session not found or expired';
      setError(`Failed to join session: ${errorMessage}`);
      setIsInitializing(false);
    }
  };

  const {
    messages,
    isConnected,
    error: firebaseError,
    sessionMetadata,
    serverPresence,
    serverData,
    restaurantData,
    sessionStatus,
    chatStatus,
    joinSession,
    sendMessage: sendFirebaseMessage,
    logLinkClick,
    sendOptInCode,
    verifyOptInCode
  } = useFirebaseChat();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [isNearBottom, setIsNearBottom] = useState(true);
  // Simplified state - no measurement logic needed

  // Initialize from NFC when component mounts
  useEffect(() => {
    initializeFromNFC();
  }, []);

  // Remove HTML splash on mount
  useEffect(() => {
    const htmlSplash = document.getElementById('pre-react-splash');
    if (htmlSplash) {
      htmlSplash.remove();
    }
  }, []);

  // Detect keyboard open/close
  useEffect(() => {
    const handleResize = () => {
      if (window.visualViewport) {
        // If visual viewport height is significantly smaller than window height, keyboard is open
        const viewportHeight = window.visualViewport.height;
        const windowHeight = window.innerHeight;
        const keyboardOpen = viewportHeight < windowHeight * 0.75; // 75% threshold
        setIsKeyboardOpen(keyboardOpen);
      }
    };

    const viewport = window.visualViewport;
    if (viewport) {
      viewport.addEventListener('resize', handleResize);
      return () => {
        viewport.removeEventListener('resize', handleResize);
      };
    }
  }, []);

  // Request notification permissions when chat loads
  useEffect(() => {
    // Enhanced debug logging for mobile
    const urlParams = new URLSearchParams(window.location.search);
    const debugMode = urlParams.get('debug') === 'true';
    
    if (debugMode) {
      console.log('🔍 ChatInterface Debug Info:', {
        userAgent: navigator.userAgent,
        viewport: { width: window.innerWidth, height: window.innerHeight },
        screen: { width: screen.width, height: screen.height },
        devicePixelRatio: window.devicePixelRatio,
        visualViewport: window.visualViewport ? {
          width: window.visualViewport.width,
          height: window.visualViewport.height
        } : 'Not supported'
      });
    }
    
    // Always show build timestamp for cache verification
    const buildTime = new Date().toISOString();
    console.log('🚀 PWC Build Time:', buildTime);
    console.log('📱 Layout Mode: Flexbox Messages');
    console.log('🔧 Messages container should be scrollable');
    
    // Make build time globally accessible for cache debugging
    (window as any).PWC_BUILD_TIME = buildTime;
    
    // Debug font sizes after DOM is ready
    const debugFontSizes = () => {
      const messageEl = document.querySelector('[data-message="true"] .MuiTypography-root');
      const inputEl = document.querySelector('.MuiInputBase-input');
      
      if (messageEl && inputEl) {
        const messageStyles = getComputedStyle(messageEl);
        const inputStyles = getComputedStyle(inputEl);
        
        console.log('🔍 Font Size Debug:');
        console.log('📝 Message bubble:', {
          fontSize: messageStyles.fontSize,
          lineHeight: messageStyles.lineHeight,
          fontFamily: messageStyles.fontFamily,
          padding: messageStyles.padding
        });
        console.log('⌨️  Input field:', {
          fontSize: inputStyles.fontSize,
          lineHeight: inputStyles.lineHeight,
          fontFamily: inputStyles.fontFamily,
          padding: inputStyles.padding
        });
      }
    };
    
    // Run debug after components are mounted
    setTimeout(debugFontSizes, 1000);
    
    // Request permissions after a short delay to avoid blocking initial load
    const timer = setTimeout(() => {
      if (notificationManager.isNotificationSupported()) {
        notificationManager.requestPermissions().then(granted => {
          console.log('Notification permissions:', granted ? 'granted' : 'denied');
        });
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, []);



  // Removed measurement logic - CSS handles all layout

    // Removed all keyboard detection - CSS Grid handles viewport changes automatically

  // Removed layout recalculation logic - CSS Grid handles this automatically

  // Removed keyboard-specific scroll logic - messages auto-scroll naturally

  // Auto-scroll to bottom when new messages arrive - SAFE method
  const scrollToBottom = (smooth = true) => {
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      if (smooth) {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth'
        });
      } else {
        container.scrollTop = container.scrollHeight;
      }
    }
  };



  // Check if user is near bottom of messages
  const checkScrollPosition = () => {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      setIsNearBottom(distanceFromBottom < 100);
    }
  };

  // REMOVED: Legacy fallback token generation
  // This was causing duplicate tokens when Firebase connection failed
  // All tokens must come from backend - no client-side generation allowed

  // Auto-scroll when new messages arrive (only if user is near bottom)
  useEffect(() => {
    if (isNearBottom) {
      const timer = setTimeout(() => scrollToBottom(), 100);
      return () => clearTimeout(timer);
    } else {
    }
  }, [messages, isNearBottom]);

  // Scroll to bottom on initial load
  useEffect(() => {
    if (messages.length > 0) {
      setTimeout(() => scrollToBottom(false), 500);
    }
  }, [session?.sessionId]);

  const handleSendMessage = async (text: string) => {
    try {
      await sendFirebaseMessage(text);
    } catch (error) {
      console.error('Send message error:', error);
      // Error is handled by the Firebase hook
    }
  };

  const handleRetryMessage = async (_messageId: string) => {
    try {
      // For Firebase implementation, we don't have retry logic yet
      // Messages are sent directly to Firestore
      console.log('Retry not implemented for Firebase messages');
    } catch (error) {
      console.error('Retry message error:', error);
    }
  };

  const handleRetryConnection = () => {
    clearError();
    isInitializedRef.current = false;
    initializeFromNFC();
  };

  // Menu handlers
  const handleMenuClick = () => {
    setShowMenu(true);
  };

  const handleReport = () => {
    setShowReportModal(true);
  };

  const handleMessageManagement = () => {
    setShowReportModal(true);
  };

  const handleReportSubmit = async (reportMessage: string) => {
    try {
      // Format chat history as simple text
      const chatHistory = messages.map(msg => 
        `[${msg.timestamp.toLocaleString()}] ${msg.senderName || msg.sender}: ${msg.content.text}`
      ).join('\n');

      // Submit report with simplified data
      const response = await fetch('https://nfc-session-docker-625915153805.us-central1.run.app/api/sessions/report', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId: session?.sessionId,
          patronMessage: reportMessage,
          chatHistory,
          restaurantName: restaurantData.name,
          tableNumber: session?.tableNumber,
          serverId: sessionMetadata?.metadata?.serverId,
          serverName: serverData.name,
          reportType: 'guest'
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit report');
      }

      // Success - let the modal close naturally and ReportModal will handle any success feedback

    } catch (error) {
      console.error('Report submission failed:', error);
      throw error; // Let ReportModal handle the error display
    }
  };


  const handleToastClose = () => {
    setToast(prev => ({ ...prev, show: false }));
  };

  // Opt-In Modal handlers
  const handleOptInClick = () => {
    setShowOptInModal(true);
  };

  const handleSendCode = async (data: { email?: string; phoneNumber?: string }) => {
    try {
      const result = await sendOptInCode(data);
      return result;
    } catch (error) {
      console.error('Send code failed:', error);
      throw error;
    }
  };

  const handleVerifyCode = async (data: { email?: string; phoneNumber?: string; code: string }) => {
    try {
      const result = await verifyOptInCode(data);
      return result;
    } catch (error) {
      console.error('Verify code failed:', error);
      throw error;
    }
  };

  // Removed loading state - direct rendering

  // Check if error indicates session ended
  const errorMessage = error?.toString() || error || '';
  const isSessionEnded = errorMessage && (
    errorMessage.includes('Session not found or expired') ||
    errorMessage.includes('terminated') ||
    errorMessage.includes('ended')
  );
  
  // Debug logging
  if (error) {
    console.log('🔍 Error check:', { 
      error, 
      errorMessage, 
      isSessionEnded,
      errorType: typeof error 
    });
  }

  // Determine which content to render
  const showSessionEnded = isSessionEnded;
  const showError = !showSessionEnded && error && !session;
  const showNoSession = !showSessionEnded && !showError && !isInitializing && !session && !error;
  const showChat = !showSessionEnded && !showError && !showNoSession;

  return (
    <>
    {showSessionEnded && <SessionEnded onRetry={handleRetryConnection} />}

    {showError && (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          backgroundColor: '#f8f9fa',
          px: 3,
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
        }}
      >
        <Box
          sx={{
            backgroundColor: 'white',
            borderRadius: 3,
            p: 4,
            maxWidth: 400,
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            border: '1px solid rgba(255,255,255,0.2)'
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              mx: 'auto',
              mb: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              backgroundColor: '#fff5f5',
              boxShadow: '0 4px 20px rgba(255,107,107,0.2)'
            }}
          >
            <img 
              src={forumsIcon} 
              alt="Sivousplate" 
              style={{ 
                width: 50, 
                height: 50,
                objectFit: 'contain',
                filter: 'hue-rotate(10deg) saturate(1.2)'
              }} 
            />
          </Box>

          <Typography 
            variant="h5" 
            sx={{ 
              mb: 2, 
              fontWeight: 600,
              color: '#2c3e50'
            }}
          >
            Service Temporarily Unavailable
          </Typography>
          
          <Typography 
            variant="body1" 
            sx={{ 
              mb: 3, 
              color: '#64748b',
              lineHeight: 1.6
            }}
          >
            We're having trouble connecting you to your server right now. This might be because:
          </Typography>

          <Box sx={{ mb: 3, textAlign: 'left' }}>
            <Typography variant="body2" sx={{ mb: 1, color: '#64748b', display: 'flex', alignItems: 'center' }}>
              • Your table may need to be reset by restaurant staff
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, color: '#64748b', display: 'flex', alignItems: 'center' }}>
              • Your server may be temporarily away
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, color: '#64748b', display: 'flex', alignItems: 'center' }}>
              • There may be a temporary technical issue
            </Typography>
          </Box>

          <Typography 
            variant="body2" 
            sx={{ 
              mb: 3, 
              color: '#64748b',
              fontStyle: 'italic'
            }}
          >
            Please try again in a moment, or ask a staff member for assistance.
          </Typography>

          <Button
            variant="contained"
            startIcon={<RefreshOutlined />}
            onClick={handleRetryConnection}
            size="large"
            sx={{
              backgroundColor: '#4f46e5',
              '&:hover': {
                backgroundColor: '#4338ca'
              },
              borderRadius: 2,
              py: 1.5,
              px: 3,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(79,70,229,0.3)'
            }}
          >
            Try Again
          </Button>

          <Typography 
            variant="caption" 
            sx={{ 
              mt: 3, 
              display: 'block',
              color: '#94a3b8'
            }}
          >
            Need immediate assistance? Please flag down a staff member.
          </Typography>
        </Box>
      </Box>
    )}

    {showNoSession && (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          backgroundColor: 'background.default',
          px: 3
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
          No Active Session
        </Typography>
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ textAlign: 'center', mb: 3 }}
        >
          Please scan the NFC tag from your table to start chatting with your server.
        </Typography>
        <Button
          variant="contained"
          onClick={handleRetryConnection}
          size="large"
        >
          Retry Connection
        </Button>
      </Box>
    )}

    {showChat && <Box
      sx={{
        // Flexbox layout - industry standard SMS-style
        display: 'flex',
        flexDirection: 'column',
        height: '100dvh', // Dynamic viewport height - respects keyboard
        backgroundColor: 'background.default',
        overflow: 'hidden',
        // Fallback for older browsers
        '@supports not (height: 100dvh)': {
          height: '100vh'
        },
        // Safe area support for all devices
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingLeft: 'env(safe-area-inset-left, 0px)',
        paddingRight: 'env(safe-area-inset-right, 0px)',
        // Mobile optimizations
        touchAction: 'pan-y',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        WebkitTouchCallout: 'none',
        overscrollBehavior: 'none'
      }}
    >
      {/* Header - Fixed height, no shrinking */}
      <Box sx={{ flexShrink: 0 }}>
        <ChatHeader
          restaurant={{
            id: sessionMetadata?.restaurantId || restaurant?.id || '',
            name: restaurantData.name || 'Restaurant', // Use live restaurant data
            address: restaurantData.address || '',
            city: restaurantData.city || '',
            state: restaurantData.state || '',
            logoUrl: restaurantData.logoUrl || '',
            status: restaurant?.status || 'active'
          }}
          server={{
            id: sessionMetadata?.metadata?.serverId || server?.id || '',
            name: serverData.displayName || 'Server', // Use real-time server data only
            status: serverPresence.isOnline ? 'active' : 'inactive',
            lastSeen: serverPresence.lastSeen
          }}
          tableNumber={(() => {
            // Prioritize live sessionMetadata (real-time updates) over stale session (set once at init)
            const tableNumber = sessionMetadata?.tableNumber || session?.tableNumber || 'Unknown';
            console.log('🏓 Table number debug (real-time priority):', {
              sessionMetadataTableNumber: sessionMetadata?.tableNumber,
              sessionTableNumber: session?.tableNumber,
              finalTableNumber: tableNumber,
              reasoning: 'Using sessionMetadata.tableNumber first (live real-time updates from Firestore)'
            });
            return tableNumber;
          })()}
          onMenuClick={handleMenuClick}
        />
      </Box>

      {/* Error Alert */}
      {(error || firebaseError) && (
        <Fade in>
          <Container maxWidth="sm" sx={{ pt: 1 }}>
            <Alert 
              severity="error" 
              onClose={clearError}
              sx={{ mb: 1 }}
            >
              {error || firebaseError}
            </Alert>
          </Container>
        </Fade>
      )}

      {/* Build timestamp for cache verification (debug mode only) */}
      {new URLSearchParams(window.location.search).get('debug') === 'true' && (
        <Box sx={{ 
          position: 'fixed', 
          top: '10px', 
          right: '10px', 
          backgroundColor: 'rgba(0,0,0,0.7)', 
          color: 'white', 
          padding: '4px 8px', 
          borderRadius: '4px', 
          fontSize: '10px', 
          zIndex: 9999,
          fontFamily: 'monospace'
        }}>
          Build: {new Date().toLocaleTimeString()}
        </Box>
      )}

      {/* Messages Container - Fills all leftover space */}
      <Box
        ref={messagesContainerRef}
        onScroll={checkScrollPosition}
        data-scrollable="true"
        sx={{
          // Flex: 1 - fills all space between header and input
          flex: 1,
          overflowY: 'auto', // Scrollable message history
          overflowX: 'hidden',
          backgroundColor: 'background.paper',
          WebkitOverflowScrolling: 'touch',
          scrollBehavior: 'smooth',
          overscrollBehavior: 'contain',
          p: 0,
          minHeight: 0 // Important for flex to work properly
        }}
      >
        <Container maxWidth="sm" sx={{ 
          px: 1.5,
          py: 1.5,
          minHeight: '100%',
          // Step 1: Enable flexbox layout
          display: 'flex',
          // Step 2: Stack messages vertically
          flexDirection: 'column',
          // Step 3: Smart positioning - few messages at bottom, many at top
          justifyContent: messages.length <= 3 ? 'flex-end' : 'flex-start'
        }}>
          {/* Empty state */}
          {messages.length === 0 && !isKeyboardOpen && (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                px: 3,
                textAlign: 'center'
              }}
            >
              <Typography variant="h6" sx={{ mb: 1, color: 'text.secondary' }}>
                TableChat<sup style={{ fontSize: '0.5em', verticalAlign: 'super' }}>&trade;</sup> Started
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                Send a message to your server below in your language of choice.
              </Typography>
            </Box>
          )}

          {/* Messages */}
          {messages.map((message) => (
            <Box key={message.id} sx={{ mb: 0.75 }}>
              <MessageBubble
                message={message}
                onRetry={handleRetryMessage}
                onLinkClick={logLinkClick}
                onOptInClick={handleOptInClick}
              />
            </Box>
          ))}

          {/* Scroll anchor - positioned right above input like Google Messages */}
          <div ref={messagesEndRef} style={{ height: '8px' }} />
        </Container>
      </Box>


      {/* Message Input - Fixed height, pinned to bottom */}
      <Box
        sx={{
          // Flex-shrink: 0 ensures it doesn't collapse
          flexShrink: 0,
          backgroundColor: 'background.paper',
          borderTop: (theme) => 
            `1px solid ${theme.palette.mode === 'light' ? '#e0e0e0' : '#424242'}`,
          paddingTop: '8px',
          paddingBottom: '8px',
          boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
          // Ensure it stays above other content
          zIndex: 100
        }}
      >
        <MessageInput
          onSendMessage={handleSendMessage}
          disabled={!isConnected || sessionStatus === 'ended' || sessionStatus === 'terminated' || chatStatus === 'disabled'}
          placeholder={
            !isConnected 
              ? "Connecting..." 
              : sessionStatus === 'ended'
              ? "Chat session has ended"
              : sessionStatus === 'terminated'
              ? "Session has expired"
              : chatStatus === 'disabled'
              ? "Chat has been disabled"
              : `Message ${serverData.displayName || 'your server'}...`
          }
        />
      </Box>

      {/* Patron Menu */}
      <PatronMenu
        open={showMenu}
        onClose={() => setShowMenu(false)}
        onReport={handleReport}
        onMessageManagement={handleMessageManagement}
      />

      {/* Report Modal */}
      <ReportModal
        open={showReportModal}
        onClose={() => setShowReportModal(false)}
        onSubmit={handleReportSubmit}
        messages={messages}
        restaurantName={restaurantData.name || 'Unknown Restaurant'}
        tableNumber={session?.tableNumber || 'Unknown'}
        serverName={serverData.displayName || 'Unknown Server'}
      />

      {/* Opt-In Modal */}
      <OptInModal
        open={showOptInModal}
        onClose={() => setShowOptInModal(false)}
        onSendCode={handleSendCode}
        onVerifyCode={handleVerifyCode}
        restaurantName={restaurantData.name || 'Restaurant'}
        restaurantLogoUrl={restaurantData.logoUrl}
        promotionalMessage={
          // Get the promotional message from the opt-in request message metadata
          messages.find(m => m.metadata?.isOptInRequest)?.content.text || 
          'Join our loyalty program and get exclusive offers!'
        }
      />

      {/* Toast Notifications */}
      <Toast
        open={toast.show}
        message={toast.message}
        severity={toast.severity}
        onClose={handleToastClose}
      />
    </Box>}
    </>
  );
};

export default ChatInterface;
